import gql from 'graphql-tag';
import {
    ArticleSummary,
    ChannelSubscriptionType,
    ChannelSubscriptionWithPaymentInfo,
    RecommendedArticlesResult,
    ShortDashboardUser,
    ShortUser,
    Subscriptions,
} from '../shared/types';
import { RECOMMENDED_ARTICLES_SLICE_FRAGMENT } from './articles';

export const USERS_PAGINATED = gql`
    query getPaginatedUsers(
        $params: UsersInput
        $includeActiveSubscriptions: Boolean! = false
    ) {
        getPaginatedUsers(params: $params) {
            users {
                _id
                lastLogin
                role
                name
                company
                phone
                position
                email
                articleCount
                views
                trialMinLeft
                activeChannelSubscriptionsCount
                    @include(if: $includeActiveSubscriptions)
            }
            total
        }
    }
`;

export const PAGINATED_USERS_SEARCH = gql`
    query getPaginatedUsers($params: UsersInput) {
        getPaginatedUsers(params: $params) {
            users {
                _id
                scopes
                name
                email
            }
        }
    }
`;

export const USER = gql`
    query findUserById($id: String!) {
        document: findUserById(id: $id) {
            _id
            version
            firstLogin
            lastLogin
            scopes
            role
            name
            company
            phone
            position
            email
            displayAvatar
            tableauAccess
        }
    }
`;

export const ADMIN_USER_CHANNEL_SUBSCRIPTIONS = gql`
    query userChannelSubscriptions($userId: String!) {
        subscriptions: userChannelSubscriptions(userId: $userId) {
            channelId
            subscriptionState
            subscriptionType
            createdAt
            updatedAt
            cancelAt
            cancelRequestedAt
            channel {
                _id
                name
                owner {
                    _id
                    name
                }
            }
        }
    }
`;

export const USER_ARTICLE_VIEWS_BY_CHANNEL = gql`
    query findUserById($id: String!, $channelId: ID!) {
        user: findUserById(id: $id) {
            _id
            articleViewsByChannel(channelId: $channelId)
        }
    }
`;

export const USER_PUBLISH_CHANNELS = gql`
    query me {
        me {
            channelsToPublish {
                _id
                name
                ownerId
                ownerType
                type
                owner {
                    _id
                    name
                }
            }
        }
    }
`;

export const USER_CHANNEL_SUBSCRIPTIONS = gql`
    query me {
        me {
            channelSubscriptions {
                subscribed
                subscriptionType
                subscriptionState
                subscriptionId
                channel {
                    _id
                    name
                    price
                    owner {
                        _id
                        name
                    }
                    ownerType
                    ownerId
                    coverImage
                    type
                }
                nextPayment
                paymentMethod {
                    type
                    details
                }
                cancelAt
                cancelAtPeriodEnd
                rebate
                pricePerMonth
            }
        }
    }
`;

export const FOR_YOU_REQUEST_MORE = gql`
	query me($take: Int, $skip: Int) {
		me {
			recommendedArticles(take: $take, skip: $skip) {
				${RECOMMENDED_ARTICLES_SLICE_FRAGMENT}
			}
		}
	}`;

export const USER_SUBSCRIPTIONS = gql`
    query me {
        me {
            subscriptions {
                categories {
                    category {
                        _id
                        name
                    }
                }
            }
        }
    }
`;

export const CHANNEL_ADMINS = gql`
    query getUsers {
        documents: channelAdmins {
            _id
            scopes
            name
        }
    }
`;

export const USERS = gql`
    query getUsers {
        documents: users {
            _id
            name
            email
            scopes
        }
    }
`;

export const MY_BILLING_ACCOUNTS = gql`
    query myBillingAccounts($filter: String) {
        myBillingAccounts(filter: $filter) {
            id
            name
        }
    }
`;

export const USER_INTERESTS = gql`
    query getUserInterests {
        getUserInterests
    }
`;

export const GET_USER_PREMIUM_CHANNEL_SUBSCRIPTIONS = gql`
    query getPremiumChannelsSubscriptions {
        premiumChannelSubscriptions: getPremiumChannelsSubscriptions {
            channelId
            subscriptionState
            createdAt
            updatedAt
            subscriptionId
            subscriptionType
            cancelAt
        }
    }
`;

export const GET_USER_EMAIL_NOTIFICATION_SETTINGS = gql`
    query me {
        me {
            emailNotificationSettings {
                channelNotifications
            }
        }
    }
`;

export const EDIT_USER = gql`
    mutation editUser($item: UserInputEdit) {
        editUser(item: $item) {
            _id
            version
            position
            displayAvatar
        }
    }
`;

export const DELETE_USER = gql`
    mutation deleteUser($id: String!) {
        deleteUser(id: $id) {
            _id
        }
    }
`;

export const UPDATE_USER_FROM_TOKEN = gql`
    mutation updateUserFromToken($idToken: String!) {
        updateUserFromToken(idToken: $idToken) {
            user {
                _id
                version
                firstLogin
                lastLogin
                scopes
                role
                name
                company
                phone
                position
                email
                subscriptions {
                    categories {
                        category {
                            rootCategory {
                                name
                            }
                        }
                    }
                }
                channelSubscriptions {
                    channel {
                        name
                    }
                    channelId
                    subscriptionState
                }
            }
            isNewUser
        }
    }
`;

export const CREATE_STRIPE_PAYMENT_SETUP_INTENT = gql`
    mutation createPaymentSetupIntent($subscriptionId: String!) {
        createPaymentSetupIntent(subscriptionId: $subscriptionId) {
            clientSecret
            billingAddress {
                city
                country
                line1
                line2
                postalCode
                state
            }
            metadata {
                businessName
                taxId
            }
        }
    }
`;

export const SET_USER_EMAIL_NOTIFICATION_SETTINGS = gql`
    mutation setUserEmailNotificationSettings(
        $item: EmailNotificationSettingsInput
    ) {
        setUserEmailNotificationSettings(item: $item)
    }
`;

export const SET_PREMIUM_CHANNELS_EMAIL_NOTIFICATION_SETTINGS = gql`
    mutation setPremiumChannelEmailNotificationSettings($channelId: ID!) {
        setPremiumChannelEmailNotificationSettings(channelId: $channelId)
    }
`;

export interface StripePaymentSetupIntent {
    createPaymentSetupIntent: {
        clientSecret: string;
        billingAddress?: {
            city: string;
            country: string;
            line1: string;
            line2: string;
            postalCode: string;
            state: string;
        };
        metadata?: {
            businessName: string;
            taxId: string;
        };
    };
}

export interface CreateChannelSubscriptionInput {
    channelId: string;
}

export interface UserSubscriptionsResponse {
    me: {
        subscriptions: Subscriptions;
        channelSubscriptions: ChannelSubscriptionWithPaymentInfo[];
    };
}

export interface ForYouResponse {
    me: {
        subscriptions: Subscriptions;
        channelSubscriptions: ChannelSubscriptionWithPaymentInfo[];
        recommendedArticles: RecommendedArticlesResult<ArticleSummary>;
    };
}

export interface AdminUser {
    _id: string;
}

export type ChannelSubscribeInput =
    AccountChannelSubscribeInput | StripeChannelSubscribeInput;
export type ChannelUnsubscribeInput =
    AccountChannelSubscribeInput | StripeChannelSubscribeInput;

interface ChannelSubscribeInputBase {
    channelId: string;
    subscriptionType: ChannelSubscriptionType;
}

interface AccountChannelSubscribeInput extends ChannelSubscribeInputBase {
    accountNumber: string;
}

interface StripeChannelSubscribeInput extends ChannelSubscribeInputBase {
    subscriptionId: string;
}

export const CHECK_EMAILS = gql`
    query checkEmails($emails: [String!]!, $channelId: String!) {
        checkEmails(emails: $emails, channelId: $channelId) {
            unmatched
            users {
                _id
                email
                name
                company
            }
            existing {
                _id
                email
                name
                company
            }
            neonUsers {
                neonUserId
                auth0email
                firstName
                lastName
                company
            }
        }
    }
`;

export interface CheckEmailResponse {
    checkEmails: {
        unmatched: string[];
        users: ShortUser[];
        existing: ShortUser[];
        neonUsers: ShortDashboardUser[];
    };
}
